import { useLingui } from '@lingui/react/macro';
import { Image } from '@/routes/-components/image';
import { Suspense } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';

/**
 * The funded-projects figure, live from the same query the homepage counts on. Its own component so the
 * Suspense boundary wraps the number alone — the photo and the headline beside it need no data and
 * should not wait for one.
 */
function ProjectsFunded() {
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	const { language } = useTranslation();
	return <>{stats.projectsFunded.toLocaleString(language, { useGrouping: true })}+</>;
}

/**
 * "Una historia con impacto social" — the About hero.
 *
 * Two columns off the hand-off: a 899x645 photo carrying the headline, and a 437-wide stack of the app
 * card over the figure card. Both columns come to the same height, which is why the right one splits
 * 373 + 248 with the same 24px gutter that separates the columns.
 */
export function Hero() {
	const { t } = useLingui();
	return (
		<section className="mx-auto grid max-w-[1440px] gap-6 px-6 pt-6 pb-16 md:pb-24 lg:grid-cols-[899fr_437fr] xl:px-10">
			{/* The overlay is the hand-off's own: black at 20% over the photograph, so white type stays
			    legible whatever the crop underneath happens to be. */}
			<div className="relative flex min-h-[420px] items-end overflow-hidden rounded-3xl lg:min-h-[645px]">
				<Image
					src="/redesign/about/hero-hands.jpg"
					alt={t`An adult hand holding a baby's hand`}
					height={1290}
					aspectRatio={899 / 645}
					objectFit="cover"
					className="absolute inset-0 size-full object-cover!"
				/>
				<div aria-hidden className="absolute inset-0 bg-black/20" />
				<div className="relative flex flex-col gap-4 p-6 md:p-14">
					<h1 className="max-w-[546px] text-4xl leading-[1.143] font-medium tracking-[-0.02em] text-white md:text-[3.5rem]">
						{t`A story with social impact`}
					</h1>
					<p className="max-w-[584px] text-base leading-[1.5] text-white">
						{t`What if we could transform the world every time we use our card?`}
					</p>
				</div>
			</div>

			<div className="flex flex-col gap-6">
				{/* #FFEDDF is the hand-off's own value for this card and is NOT in the palette page, so it
				    stays a literal rather than borrowing `peach` (#f8ddc9), which is a different colour.

				    The export is the illustration alone, 338 wide against the card's 437. It carries its own
				    `mix-blend-mode: multiply`, but that only composites within the SVG canvas when loaded as
				    an image — so the class repeats it on the <img> to drop the white plate against the card.
				    `isolate` keeps the blend from reaching the section behind. */}
				<div className="isolate flex items-center justify-center overflow-hidden rounded-3xl bg-[#FFEDDF] lg:h-[373px]">
					<img
						src="/redesign/about/app-impact.svg"
						alt={t`The Worldcoo app showing a donor's impact`}
						className="h-auto w-[77%] mix-blend-multiply"
					/>
				</div>

				<div className="bg-ink flex flex-col justify-between gap-8 rounded-3xl p-8 lg:h-[248px] lg:px-10">
					<Zap className="size-8 shrink-0 fill-current text-white" strokeWidth={0} />
					<div className="flex flex-col">
						<p className="text-olive text-5xl leading-[1.3] font-medium tabular-nums md:text-[4rem]">
							<Suspense fallback={<span className="opacity-40">—</span>}>
								<ProjectsFunded />
							</Suspense>
						</p>
						<p className="text-xl leading-[1.5] font-medium text-white/70 md:text-2xl">{t`Funded projects`}</p>
					</div>
				</div>
			</div>
		</section>
	);
}
